/**
 * comment.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { Observable } from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { IntakeStep } from "../algorithms/structure/intake-step";
import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { MessageReceiver } from  "../server/message-receiver";
import {
  AddCommentRequest,
  CommentResponse,
  DeleteCommentRequest,
  DeprecatedAddStepCommentRequest,
  DeprecatedDeleteStepCommentRequest,
  DeprecatedModifyStepCommentRequest,
  ModifyCommentRequest
} from "../server/messages/collaboration/";
import { MessageTag } from "../server/messages/message-tag";
import { MessagingService } from "../server/messaging.service";
import { onDisconnect } from "../support/types/decorators";
import { Comment, CommentType, Visibility } from "./comment";

@Injectable()
export class CommentService implements MessageReceiver
{
  private comments : Map<[CommentType, number], Comment[]> =
    new Map<[CommentType, number], Comment[]>();

  public globalForm : FormGroup = new FormGroup({});
  public changesSinceSave : boolean = false;
  public selfInitiatedChange : boolean = false;

  public constructor (
    protected messaging : MessagingService,
    protected alerts : AlertService,
    protected algorithms : AlgorithmService,
    protected users : UserService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected()
    );
  }

  @onDisconnect()
  private clear () : void
  {
    this.comments.clear();
    this.globalForm = new FormGroup({});
    this.changesSinceSave = false;
    this.selfInitiatedChange = false;
  }

  /**
   * Initialization for when the {@link MessagingService} is ready.
   */
  public connected () : void
  {
    this.messaging.receive(MessageTag.RSP_COMMENT).subscribe(
      (message : CommentResponse) => this.receiveComments(message));
  }

  public disconnected () : void {/* decorator generated */}

  private receiveComments (message : CommentResponse) : void
  {
    if (!this.algorithms.isRunning)
    {
      throw new Error("Cannot set comments outside of algorithm context");
    }
    if (message.commentType === CommentType.SubjectContext)
    {
      this.algorithms.running.subject.comments = message.comments;
      if (!this.selfInitiatedChange
          && !this.algorithms.running.recentlyLaunched)
      {
        this.alerts.notify("The comments for the subject context were changed");
      }
    }
    else
    {
      this.algorithms.running.steps.comment(
        message.stepId,
        message.comments
        // this.selfInitiatedChange
      );
    }
    this.selfInitiatedChange = false;
  }

  private getGroupIds () : number[]
  {
    return this.users.self.groupIds();
  }

  public addForStep (
    stepId : number,
    comment : string,
    visibility : Visibility
  ) : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new DeprecatedAddStepCommentRequest(stepId, comment, visibility,
        this.getGroupIds())
    );
  }

  public addForSubject (
    comment : string,
    visibility : Visibility
  ) : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new AddCommentRequest(comment, visibility, this.getGroupIds()));
  }

  public updateForStep (
    stepId : number,
    comment : Comment,
    visibility : Visibility = Comment.visibilityOf(comment)
  ) : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new DeprecatedModifyStepCommentRequest(
        stepId,
        comment.commentId,
        comment.comment,
        visibility,
        this.getGroupIds()
      ));
  }

  public updateForSubject (
    comment : Comment,
    visibility : Visibility = Comment.visibilityOf(comment)
  ) : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new ModifyCommentRequest(
        comment.commentId,
        comment.comment,
        visibility,
        this.getGroupIds()
      ));
  }

  public removeForStep (stepId : number, comment : Comment)
    : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new DeprecatedDeleteStepCommentRequest(comment.commentId, stepId));
  }

  public removeForSubject (comment : Comment) : Observable<boolean>
  {
    this.setFlags();
    return this.messaging.resultOf(
      new DeleteCommentRequest(comment.commentId));
  }

  private setFlags () : void
  {
    this.changesSinceSave = true;
    this.selfInitiatedChange = true;
  }

  public resetChangeFlag () : void
  {
    this.changesSinceSave = false;
  }

  public hasUnsubmittedContent () : boolean
  {
    if (this.globalForm.dirty)
    {
      console.info(
        "Dirty comment forms:",
        Object.values(this.globalForm.controls).filter(
          (f : AbstractControl) => f.dirty)
      );
      return true;
    }
    return false;
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/comment.service.ts